import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { useExplore, hasListingImage } from "@/lib/ExploreContext";
import { listingPath } from "@/lib/businessSlug";
import { openStatus, parseWeek, formatTime, puertoRicoClock } from "@/lib/hours";
import useNow from "@/hooks/useNow";

/**
 * Local-first, time-aware recommendation module.
 * Uses Puerto Rico's timezone even when the visitor is planning from abroad.
 * Opening claims are shown ONLY for places with a structured hours_json week.
 * No guessed hours, location permission, reservations, or auto-scroll.
 */
const PR_TIMEZONE = "America/Puerto_Rico";
const visibleTime = new Intl.DateTimeFormat("en-US", {
  timeZone: PR_TIMEZONE, hour: "numeric", minute: "2-digit", hour12: true
});

function dayPeriod(hour) {
  if (hour >= 6 && hour < 11) return "morning";
  if (hour >= 11 && hour < 15) return "lunch";
  if (hour >= 15 && hour < 18) return "afternoon";
  if (hour >= 18 && hour < 22) return "dinner";
  return "late";
}
const COPY = {
  morning: {
    en: "GOOD MORNING, PR.", es: "BUEN DÍA, PR.",
    subEn: "Coffee first. Then see where the island takes you.", subEs: "Primero café. Después, que la isla decida.",
    re: /bakery|cafe|caf[eé]|coffee|breakfast|brunch|panader|pastr|comedor|park|beach|desayun/i
  },
  lunch: {
    en: "IT'S LUNCHTIME.", es: "ES HORA DE COMER.",
    subEn: "See what's open for lunch across Puerto Rico.", subEs: "Encuentra dónde almorzar en Puerto Rico.",
    re: /restaurant|gastronomy|food|seafood|lunch|comedor|criollo|kiosk|chinchorro|bakery|pizza|vegan|taco|marisco/i
  },
  afternoon: {
    en: "THE DAY ISN'T OVER.", es: "QUEDA DÍA POR VIVIR.",
    subEn: "Art, experiences, something good to eat.", subEs: "Arte, aventuras y algo rico para comer.",
    re: /culture|art|gallery|museum|beach|experience|tour|park|gastronomy|cafe|coffee/i
  },
  dinner: {
    en: "MAKE A NIGHT OF IT.", es: "HAZ UNA NOCHE ESPECIAL.",
    subEn: "Dinner, rooftops and somewhere to stay a while.", subEs: "Cena, terrazas y lugares para pasarla bien.",
    re: /restaurant|gastronomy|dinner|seafood|japanese|rooftop|cocktail|criollo|sushi|tapas|nightlife|bar/i
  },
  late: {
    en: "STILL OUT?", es: "¿SIGUES POR AHÍ?",
    subEn: "Late nights, local venues and plans for tomorrow.", subEs: "Noches largas, lugares locales y planes para mañana.",
    re: /nightlife|bar|lounge|nightclub|late.night|rooftop|salsa|live music|karaoke|dance/i
  }
};
function score(item, re, nbNames) {
  const hay = [item.category, item.subcategory, item.type, item.name, nbNames?.[item.neighborhood_id]]
    .filter(Boolean).join(" ");
  return (re.test(hay) ? 100 : 0) +
    (hasListingImage(item) ? 28 : 0) +
    (item.editor_pick ? 12 : 0) +
    (item.locals_know ? 8 : 0) +
    (item.featured ? 5 : 0);
}
function photo(item) {
  return item.storefront_image || item.cover_image ||
    (Array.isArray(item.gallery_images) ? item.gallery_images.find(Boolean) : "") || "";
}
const MODES = [
  { id: "now", en: "Open now", es: "Abiertos ahora" },
  { id: "later", en: "Up next", es: "Más tarde" },
  { id: "anytime", en: "Explore anytime", es: "Explorar siempre" }
];

export default function HomeLiveMoment() {
  const { lang } = useLang();
  const es = lang === "es";
  const { allListings, nbNames } = useExplore();
  const now = useNow();
  const [mode, setMode] = useState("now");
  const clock = useMemo(() => puertoRicoClock(now), [now]);
  const phase = COPY[dayPeriod(clock.hour)];

  const candidates = useMemo(() => {
    const published = allListings.filter((place) =>
      place?.id && place?.name && place.status === "Published" &&
      !place.coming_soon && !place.source &&
      ["Nightlife","Gastronomy","Culture","LGBTQ+","Shop","Experiences"].includes(place.category)
    );
    const present = published.map((item) => {
      const week = parseWeek(item.hours_json);
      const status = week ? openStatus(week, lang, clock) : null;
      return { item, week, status };
    });
    let chosen;
    if (mode === "now") {
      chosen = present.filter(({ status }) => status?.open && status.minutesLeft > 30);
    } else if (mode === "later") {
      // Look ahead by hour, up to 12h; a place must be closed now and then
      // actually become open according to a structured schedule.
      const futureClocks = Array.from({ length: 12 }, (_, i) =>
        puertoRicoClock(new Date(now.getTime() + (i + 1) * 60 * 60 * 1000)));
      chosen = present
        .filter(({ week, status }) => week && !status?.open)
        .map((record) => {
          const next = futureClocks.find((future) => openStatus(record.week, lang, future)?.open);
          return { ...record, future: next };
        })
        .filter(({ future }) => future);
    } else {
      chosen = present.filter(({ item }) => hasListingImage(item));
    }
    return chosen
      .sort((a, b) =>
        score(b.item, phase.re, nbNames) - score(a.item, phase.re, nbNames) ||
        String(a.item.name).localeCompare(String(b.item.name)))
      .slice(0, 7);
  }, [allListings, nbNames, lang, clock, phase, mode, now]);

  // The local pulse is intentionally light: useful status and shortcuts,
  // not a second hero competing with cultural photography.
  return (
    <section
      aria-label={es ? "Qué hacer en Puerto Rico ahora" : "What to do in Puerto Rico right now"}
      className="mx-auto mt-3 max-w-[1240px] px-4 sm:mt-5 sm:px-6 lg:px-8"
    >
      <div className="border-t border-[#171717]/25 pt-3 dark:border-white/25">
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <div className="flex min-w-0 items-baseline gap-2.5">
            <span className="shrink-0 font-mono text-[9px] font-black uppercase tracking-[.11em] text-[#a33a33] dark:text-[#ff867b]">
              {es ? "PULSO LOCAL" : "LOCAL PULSE"}
            </span>
            <h2 className="truncate text-[16px] font-black uppercase leading-tight tracking-[-.045em] text-[#171717] dark:text-[#f7f4ec] sm:text-[20px]">
              {es ? phase.es : phase.en}
            </h2>
            <span className="hidden font-mono text-[10px] font-bold text-[#817b71] dark:text-[#b5aea3] sm:inline">
              {visibleTime.format(now)} PR
            </span>
          </div>
          <div role="group" aria-label={es ? "Explorar por momento" : "Browse by time"} className="no-scrollbar flex max-w-full items-center gap-1 overflow-x-auto">
            {MODES.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setMode(tab.id)}
                aria-pressed={mode === tab.id}
                className={"min-h-9 shrink-0 whitespace-nowrap border-b-2 px-2.5 py-1.5 text-[10px] font-extrabold uppercase tracking-[.01em] transition-colors sm:text-[11px] " +
                  (mode === tab.id
                    ? "border-[#a33a33] text-[#171717] dark:text-white"
                    : "border-transparent text-[#77736c] hover:text-[#171717] dark:text-[#b9b2aa] dark:hover:text-white")}
              >
                {es ? tab.es : tab.en}
              </button>
            ))}
            <Link
              to="/map?near=1"
              className="ml-1 inline-flex h-9 shrink-0 items-center gap-1 border-b-2 border-transparent px-2 text-[10px] font-bold uppercase text-[#77736c] hover:text-[#171717] dark:text-[#c1baae]"
              aria-label={es ? "Buscar cerca de mí" : "Find places near me"}
            >
              <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
              <span className="hidden sm:inline">{es ? "Cerca" : "Near me"}</span>
            </Link>
          </div>
        </div>
        {candidates.length ? (
          <div aria-live="polite" className="no-scrollbar mt-2.5 flex snap-x snap-mandatory gap-2 overflow-x-auto pb-1 [scrollbar-width:none]">
            {candidates.map(({ item, status }) => (
              <Link
                key={item.id}
                to={listingPath(item)}
                className="group flex h-[79px] w-[min(78vw,258px)] shrink-0 snap-start overflow-hidden border border-[#dfdcd5] bg-white text-[#171717] transition-colors hover:border-[#171717]/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#a33a33] dark:border-white/20 dark:bg-[#292824] dark:text-white sm:w-[265px]"
              >
                <div className="h-full w-[83px] shrink-0 bg-[#37332f]">
                  {photo(item) ? (
                    <img src={photo(item)} alt="" loading="lazy" decoding="async"
                      onError={(event) => { event.currentTarget.style.display = "none"; }}
                      className="h-full w-full object-cover" />
                  ) : (
                    <span className="flex h-full items-center justify-center font-mono text-[9px] font-bold text-white/75">PR / LOCAL</span>
                  )}
                </div>
                <div className="flex min-w-0 flex-1 flex-col justify-center gap-1 px-2.5">
                  <span className="truncate font-mono text-[9px] font-bold uppercase tracking-[.06em] text-[#807b71] dark:text-[#c0b8a9]">
                    {nbNames?.[item.neighborhood_id] || item.subcategory || item.category}
                  </span>
                  <span className="line-clamp-2 text-[12px] font-extrabold leading-[1.2]">{item.name}</span>
                  {mode === "now" && status?.open && (
                    <span className="truncate text-[10px] font-semibold text-[#26774c] dark:text-[#9dd4aa]">
                      {es ? "Abierto · cierra " : "Open · closes "}{formatTime(status.closesAt)}
                    </span>
                  )}
                  {mode === "later" && (
                    <span className="truncate text-[10px] font-semibold text-[#996d47] dark:text-[#e9b988]">
                      {es ? "Abre " : "Opens "}{status?.opensInDays === 1
                        ? (es ? "mañana " : "tomorrow ") + formatTime(status?.opensAt)
                        : formatTime(status?.opensAt)}
                    </span>
                  )}
                </div>
                <ArrowUpRight className="mr-2 mt-2 h-3.5 w-3.5 shrink-0 text-[#8a8177] dark:text-[#c3b7aa]" aria-hidden="true" />
              </Link>
            ))}
          </div>
        ) : (
          <p className="mt-2 text-[11px] leading-[1.5] text-[#77736c] dark:text-[#bcb5a9]">
            {es ? "Aún confirmamos los horarios. Puedes explorar todos los lugares." : "We're confirming opening hours. You can still explore every place."}
            <Link to="/map" className="ml-2 font-bold underline underline-offset-2">
              {es ? "Ver mapa" : "Explore map"}
            </Link>
          </p>
        )}
      </div>
    </section>
  );
}
